import { FC } from 'react';
import { History, X, WifiOff } from 'lucide-react';

interface DraftBannerProps {
  /** Epoch ms the draft was written. */
  savedAt: number;
  /** True when the draft holds edits that never reached the server. */
  pendingSync?: boolean;
  onRestore: () => void;
  onDiscard: () => void;
}

function formatSavedAt(savedAt: number): string {
  const date = new Date(savedAt);
  const now = new Date();
  const time = date.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });

  const isSameDay =
    date.getDate() === now.getDate() && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();

  if (isSameDay) return `hari ini pukul ${time}`;

  return `${date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })} pukul ${time}`;
}

/**
 * Offers a locally saved draft for restore.
 *
 * Restoring is a user decision rather than automatic: these pages populate the
 * form from the server on load, and quietly replaying an older draft over
 * fresher server data would corrupt it with nothing on screen to explain why.
 */
const DraftBanner: FC<DraftBannerProps> = ({ savedAt, pendingSync = false, onRestore, onDiscard }) => {
  return (
    <output
      data-testid="draft-banner"
      className="mb-4 flex flex-col sm:flex-row flex-wrap sm:items-center justify-between gap-3 rounded-xl border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/30 px-4 py-3"
    >
      <div className="flex items-start gap-2.5 flex-1 min-w-[240px]">
        {pendingSync ? (
          <WifiOff size={16} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
        ) : (
          <History size={16} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
        )}
        <div className="text-sm">
          <p className="font-medium text-amber-900 dark:text-amber-200">
            {pendingSync ? 'Ada perubahan yang belum tersimpan ke server' : 'Draft tersimpan ditemukan'}
          </p>
          <p className="text-amber-700 dark:text-amber-300/80 mt-0.5">
            Terakhir disimpan {formatSavedAt(savedAt)}. Pulihkan untuk melanjutkan, atau buang untuk memakai data
            tersimpan.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0 flex-wrap">
        <button
          type="button"
          onClick={onRestore}
          data-testid="draft-restore"
          className="px-3 py-1.5 text-sm font-medium rounded-lg text-white transition-opacity hover:opacity-90"
          style={{ background: '#004EEB' }}
        >
          Pulihkan
        </button>
        <button
          type="button"
          onClick={onDiscard}
          data-testid="draft-discard"
          aria-label="Buang draft"
          className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
        >
          <X size={14} />
          Buang
        </button>
      </div>
    </output>
  );
};

export default DraftBanner;
